<template>
  <div
    :class="[$style.base, { [$style.expanded]: showDiscountField }]"
    v-test="'discount-form'"
  >
    <div :class="$style.discountFieldWrapper">
      <div :class="$style.discountInputWrapper">
        <BaseFormInput
          v-if="showDiscountField"
          v-model="discountCodeInput"
          :disabled="!isLoading"
          name="discountCode"
          :label="$t('confirmation.discount')"
          v-test="'discountCodeInput'"
        />
        <BaseButton
          :class="$style.applyDiscountButton"
          @click="fetchDiscountCode"
          v-test="'applyCodeButton'"
          v-if="showDiscountField"
          :buttonColor="buttonColor"
          :isLoading="isLoading"
        >
          {{ $t(`confirmation.${applyCodeKey}`) }}
        </BaseButton>
        <IconClose
          :class="$style.closeButton"
          v-if="showDiscountField"
          @click="closeAndCleanInput"
          v-test="'discount-form-btn-close'"
        />
      </div>
    </div>
    <div
      v-test="'btn-add-discount-code'"
      v-if="!showDiscountField"
      :class="$style.link"
      :style="{ color: widgetSettings.color }"
      @click="showDiscountField = true"
    >
      {{ $t('confirmation.add_discount_code') }}
    </div>
    <div
      v-if="discountError"
      :class="$style.error"
      v-test="'discountCodeError'"
    >
      {{ $t('errors.discount_code_not_valid') }}
    </div>
    <div
      v-if="pricing?.quote?.discount?.percentage && !discountError"
      :class="$style.success"
      v-test="'discountCodeSuccess'"
    >
      {{
        $t('confirmation.discount_code_applied', {
          percentage: -pricing.quote.discount.percentage
        })
      }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBookingStore } from '@/stores/booking';
import type { BookingFormData } from '@/types';
import { storeToRefs } from 'pinia';
import { computed, inject, ref } from 'vue';

import widgetSettings from '@/widget-settings';
import { usePrincing } from './pricing';

import BaseButton from '@/components/BaseButton.vue';
import BaseFormInput from '@/components/base-form-input/index.vue';
import IconClose from '@/components/IconClose.vue';

const { discountCode } = storeToRefs(useBookingStore());
const { pricing } = storeToRefs(useBookingStore());
const formData = inject<BookingFormData>('bookingFormData')!;

const isLoading = ref(false);
const showDiscountField = ref(false);

const alreadyHasDiscountCode = computed(
  () =>
    pricing.value?.quote.discount &&
    pricing.value.quote.discount.source === 'discount_code'
);

const discountError = computed(() =>
  pricing.value?.errors.find((e) => e.type === 'discount_code_invalid')
);
const buttonColor = computed(() =>
  alreadyHasDiscountCode.value && !discountError.value
    ? '#2ac77c'
    : widgetSettings.color
);
const applyCodeKey = computed(() =>
  alreadyHasDiscountCode.value && !discountError.value
    ? 'code_applied'
    : 'apply_discount_code'
);

const discountCodeInput = ref('');

const fetchPrice = () => {
  const { getPrice } = usePrincing();
  discountCode.value = {
    code: discountCodeInput.value
  };
  getPrice({ email: formData.email })
    .then(() => {
      if (pricing.value?.quote?.discount?.source === 'discount_code') {
        discountCode.value = {
          code: discountCodeInput.value,
          secret: pricing.value.quote.discount.session_secret,
          percentage: pricing.value.quote.discount.percentage
        };
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const fetchDiscountCode = (e: any) => {
  e.preventDefault();
  isLoading.value = true;
  fetchPrice();
};

const clearDiscountCode = () => {
  discountCodeInput.value = '';
  discountCode.value = undefined;
};

const closeAndCleanInput = () => {
  showDiscountField.value = false;
  clearDiscountCode();
  if (pricing.value?.quote?.discount?.session_secret) {
    pricing.value.quote.discount.session_secret = '';
  }
  fetchPrice();
};
</script>

<style lang="scss" module>
.base {
  margin-top: $spacing;

  &:not(.expanded) {
    display: inline-block;
    margin-right: $spacing;
  }
  &.expanded {
    width: 100%;
  }
}
.button {
  margin-top: $spacing * 2;
  width: 100%;
}

.link {
  @include link;
  background: transparent;
  margin-bottom: $spacing;
  text-decoration: none;
}

.discountFieldWrapper {
  display: flex;
  align-items: center;
}

.closeButton {
  cursor: pointer;
  margin-left: $spacing * 0.5;
  margin-bottom: $spacing;
}

.discountInputWrapper {
  flex-grow: 1;
  display: flex;
  position: relative;
  align-items: center;
}

.applyDiscountButton {
  white-space: nowrap;
  margin: 0 $spacing * 0.5 $spacing;
}

.error {
  color: $color-error;
  font-size: 13px;
  margin-top: -$spacing * 0.5;
  margin-bottom: $spacing * 1.2;
}

.success {
  color: $color-success;
  font-size: 14px;
  margin-top: -$spacing * 0.5;
  margin-bottom: $spacing * 1.2;
}
</style>
